import { useState } from "react";
import { useStore } from "react-redux";
import { AgentMlsIdResource } from "api/resource/agent-mls-id";
import { ClientBoardResource } from "api/resource/client-board";
import { createMlsIdAgentBoard, deleteAgentMlsId, deleteMlsIdAgentBoard, fetchAgentMlsIds, updateMlsIdClientBoard } from "redux/slice/agent-mls-ids";
import { fetchClientBoards } from "redux/slice/client-boards";
import { getAgentMlsIds } from "redux/selector";
import { RootState, ThunkAppDispatch, useAppThunkDispatch } from "redux/store";
import { AgentMlsIds } from "model/agent-mls-ids";
import { MlsIdErrorContext, mlsIdErrorMessage } from "./errors";
import { FixStep } from "./fix-steps";

// When the MLS IDs changed between opening the fix and saving it. Nothing was saved.
export const FIX_CHANGED = "This MLS ID changed since you opened it. Check it and try again.";
// When a fix stopped after some of its changes were saved.
export const FIX_PARTLY_SAVED = "Some of these changes were saved, but not all. Check this MLS ID and try again.";

// Takes a client-board row off Featured by setting its MLS ID and Backup Office MLS ID to "None". The row stays:
// a client has a board only while it has a client-board row, and deleting the last one would drop the board's
// paperwork and access.
export const takeOffFeatured = (row: ClientBoardResource) => {
	return updateMlsIdClientBoard({ clientBoard: { ...row, agentId: "None", officeId: "None" } });
};

const runStep = (dispatch: ThunkAppDispatch, agentMlsIds: AgentMlsIds, step: FixStep): Promise<AgentMlsIdResource | unknown> => {
	switch(step.kind) {
		case "rowToNone": {
			const row = agentMlsIds.rows.get(step.clientBoardId);
			return row ? dispatch(takeOffFeatured(row)).unwrap() : Promise.reject({ status: 404 });
		}
		case "deleteRow": {
			const entry = agentMlsIds.entries.find((e) => e.id === step.entryId);
			return entry
				? dispatch(deleteMlsIdAgentBoard({ agentBoard: { id: entry.id, agentId: entry.agentId, boardId: entry.boardId, agentCode: entry.mlsId } })).unwrap()
				: Promise.reject({ status: 404 });
		}
		case "addAgentBoard":
			return dispatch(createMlsIdAgentBoard({ agentBoard: { agentId: step.agentId, boardId: step.boardId, agentCode: step.mlsId } })).unwrap();
		case "delete":
			return dispatch(deleteAgentMlsId({ id: step.entryId, refetch: false })).unwrap();
	}
};

export interface FixRunOptions {
	// Builds the fix's steps from the MLS IDs; null when the fix no longer applies.
	plan: (agentMlsIds: AgentMlsIds) => FixStep[] | null;
	// Names the MLS ID and board in a refusal.
	context?: MlsIdErrorContext;
	// Every step landed. last is the last step's answer (the entry, for a PUT or POST).
	onDone: (last: unknown) => void;
	// Some steps landed before one failed. The page has reloaded, so the dialog may no longer apply.
	onPartial: (message: string) => void;
}

// Runs a fix's steps one after another. It reloads the MLS IDs and plans again first, and stops if the plan changed
// from the one the dialog showed. It stops at the first failure: with nothing saved the dialog shows the error;
// with some steps saved it hands back the partly-saved message. It reloads both reads once the run ends either way.
export const useFixRun = (agentMlsIds: AgentMlsIds) => {
	const dispatch = useAppThunkDispatch();
	const store = useStore<RootState>();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const reload = () => Promise.all([dispatch(fetchAgentMlsIds()).unwrap(), dispatch(fetchClientBoards()).unwrap()]);

	const run = async ({ plan, context, onDone, onPartial }: FixRunOptions) => {
		const planned = plan(agentMlsIds);
		if (!planned) {
			setError(FIX_CHANGED);
			return;
		}
		setSaving(true);
		setError(null);
		let fresh: AgentMlsIds | null = null;
		try {
			await reload();
			fresh = getAgentMlsIds(store.getState());
		} catch(e) {
			fresh = null;
		}
		const steps = fresh && plan(fresh);
		if (!fresh || !steps || JSON.stringify(steps) !== JSON.stringify(planned)) {
			setError(FIX_CHANGED);
			setSaving(false);
			return;
		}
		let done = 0;
		let last: unknown;
		try {
			for (const step of steps) {
				last = await runStep(dispatch, fresh, step);
				done++;
			}
		} catch(e) {
			reload().catch(() => undefined);
			setSaving(false);
			if (done === 0) {
				setError(mlsIdErrorMessage(e, context));
			} else {
				onPartial(FIX_PARTLY_SAVED);
			}
			return;
		}
		reload().catch(() => undefined);
		setSaving(false);
		onDone(last);
	};

	const reset = () => {
		setSaving(false);
		setError(null);
	};
	return { saving, error, setError, run, reset };
};
